import React, { useState, useEffect } from 'react';
import { Home, User, Mail, Lock, MapPin, Calendar, CheckCircle, LogOut, Edit, Eye, Phone, Building } from 'lucide-react';

const ClerkshipConnectApp = () => {
  const [view, setView] = useState('landing');
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedRotation, setSelectedRotation] = useState(null);
  const [userType, setUserType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showTOS, setShowTOS] = useState(false);
  const [acceptedTOS, setAcceptedTOS] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    institution: '',
    firstName: '',
    lastName: '',
    gender: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [coordinatorLoginData, setCoordinatorLoginData] = useState({
    email: '',
    password: ''
  });
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [hostData, setHostData] = useState({
    city: '',
    region: '',
    zipCode: '',
    address: '',
    description: '',
    amenities: '',
    availability: '',
    availableFrom: '',
    availableTo: '',
    visibleToStudents: true,
    displayPublicly: true
  });
  const [rotatorData, setRotatorData] = useState({
    rotations: [{ school: '', region: '', zipCode: '', startDate: '', endDate: '', preferences: '' }],
    hospital: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasHostProfile, setHasHostProfile] = useState(false);
  const [hasRotatorProfile, setHasRotatorProfile] = useState(false);
  const [hostProfile, setHostProfile] = useState(null);
  const [rotatorProfile, setRotatorProfile] = useState(null);
  const [availableHosts, setAvailableHosts] = useState([]);
  const [coordinatorData, setCoordinatorData] = useState(null);
  const [showCoordinatorPasswordChange, setShowCoordinatorPasswordChange] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null, name: null });
  const [pageTransitioning, setPageTransitioning] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetPasswordData, setResetPasswordData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [distanceCalculator, setDistanceCalculator] = useState({
    selectedHost: null,
    selectedRotator: null,
    selectedRotation: null,
    distance: null,
    show: false
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const regions = [
    'Michigan',
    'Chicago', 
    'Midwest (not otherwise listed)',
    'New York/New Jersey',
    'DMV',
    'South (not otherwise listed)',
    'Florida',
    'California',
    'Texas',
    'West Coast (not otherwise listed)',
    'Northeast (not otherwise listed)'
  ];

  // Auto-dismiss notifications after 10 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

// Clear notifications and forms on view change
useEffect(() => {
  setSuccess('');
  setError('');
  
  // Clear forms when leaving signup/profile pages
  if (view === 'landing' || view === 'dashboard') {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      institution: '',
      firstName: '',
      lastName: '',
      gender: ''
    });
    setLoginData({
      email: '',
      password: ''
    });
    setCoordinatorLoginData({
      email: '',
      password: ''
    });
    setPasswordChangeData({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    });
    setShowPasswordChange(false);
  }
}, [view]);

  // Check for existing login on page load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: verificationEmail,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.expired) {
          setError(data.error);
        } else {
          setError(data.error || 'Email verification failed');
        }
        setLoading(false);
        return;
      }

      setSuccess(data.message);
      setVerificationCode('');
      
      // Auto-login: save token and user data
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        setCurrentUser(data.user);
        
        // Redirect to profile creation based on user type
        setTimeout(() => {
          if (data.user.userType === 'host') {
            navigateWithLoading('hostProfile');
          } else {
            navigateWithLoading('rotatorProfile');
          }
        }, 1000);
      } else {
        // Fallback to login if token not provided
        setTimeout(() => {
          navigateWithLoading('login');
        }, 1500);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Email verification error:', error);
      setError('Network error during verification. Please try again.');
      setLoading(false);
    }
  };

  const verifyToken = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data.user);
        
        if (data.user.isCoordinator) {
          await fetchCoordinatorDashboard(token);
          navigateWithLoading('coordinatorDashboard');
        } else {
          await checkUserProfiles(token);
          await fetchUserProfiles(token);
          navigateWithLoading('dashboard');
        }
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      localStorage.removeItem('token');
    }
    setLoading(false);
  };

  const checkUserProfiles = async (token) => {
    try {
      const response = await fetch(`${API_URL}/user/profiles`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasHostProfile(data.hasHostProfile);
        setHasRotatorProfile(data.hasRotatorProfile);
      }
    } catch (err) {
      console.error('Error checking profiles:', err);
    }
  };

  const fetchUserProfiles = async (token) => {
    const authToken = token || localStorage.getItem('token');
    
    try {
      const hostResponse = await fetch(`${API_URL}/hosts/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (hostResponse.ok) {
        const hostData = await hostResponse.json();
        setHostProfile(hostData);
      }
    } catch (err) {
      console.log('No host profile');
    }
  
    try {
      const rotatorResponse = await fetch(`${API_URL}/rotators/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (rotatorResponse.ok) {
        const rotatorData = await rotatorResponse.json();
        setRotatorProfile(rotatorData);
        
        // Fetch available hosts for rotator's cities
        if (rotatorData.rotation_details && rotatorData.rotation_details.length > 0) {
          await fetchAvailableHosts(rotatorData.rotation_details);
        }
      }
    } catch (err) {
      console.log('No rotator profile');
    }
  };

  const fetchCoordinatorDashboard = async (token) => {
    try {
      const response = await fetch(`${API_URL}/coordinator/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoordinatorData(data);
      }
    } catch (err) {
      console.error('Error fetching coordinator dashboard:', err);
    }
  };

  const fetchAvailableHosts = async (rotations) => {
    try {
      const allHosts = [];
      
      for (const rotation of rotations) {
        if (rotation.zipCode) {
          const response = await fetch(`${API_URL}/hosts/matched?zipCode=${encodeURIComponent(rotation.zipCode)}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.ok) {
            const hosts = await response.json();
            allHosts.push(...hosts.map(h => ({ ...h, searchZipCode: rotation.zipCode, searchSchool: rotation.school })));
          }
        }
      }
      
      setAvailableHosts(allHosts);
    } catch (err) {
      console.error('Error fetching hosts:', err);
    }
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateEduEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.edu$/i;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's exactly 10 digits
    return digitsOnly.length === 10;
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedDigits = digitsOnly.slice(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  };

  // Format date without timezone conversion
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    
    // Extract just the date part (YYYY-MM-DD)
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    
    // Create date using local timezone
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    // Format as "Jan 15, 2025"
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const handleInputChange = (e, setter, data) => {
    const { name, value } = e.target;
    
    // Special handling for phone number formatting
    if (name === 'phoneNumber') {
      setter({ ...data, [name]: formatPhoneNumber(value) });
    } else {
      setter({ ...data, [name]: value });
    }
    setError('');
  };

  // Page transition with loading
  const navigateWithLoading = (targetView) => {
    setPageTransitioning(true);
    setTimeout(() => {
      setView(targetView);
      setPageTransitioning(false);
      window.scrollTo(0, 0);
    }, 300);
  };

  // Contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (!validateEmail(contactForm.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Thank you for contacting us! We will get back to you soon.');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
      console.error('Contact form error:', err);
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!forgotPasswordEmail) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    if (!validateEmail(forgotPasswordEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setResetPasswordData({ ...resetPasswordData, email: forgotPasswordEmail });
        setTimeout(() => {
          navigateWithLoading('resetPassword');
        }, 2000);
      } else {
        setError(data.error || 'Failed to send reset code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Forgot password error:', err);
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!resetPasswordData.code || !resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (resetPasswordData.code.length !== 6) {
      setError('Reset code must be 6 digits');
      setLoading(false);
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetPasswordData.email,
          code: resetPasswordData.code,
          newPassword: resetPasswordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setResetPasswordData({ email: '', code: '', newPassword: '', confirmPassword: '' });
        
        // Auto-login: save token and user data
        if (data.token && data.user) {
          localStorage.setItem('token', data.token);
          setCurrentUser(data.user);
          
          // Redirect to dashboard after brief delay
          setTimeout(() => {
            navigateWithLoading('dashboard');
          }, 1000);
        } else {
          // Fallback to login if token not provided
          setTimeout(() => {
            navigateWithLoading('login');
          }, 2000);
        }
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Reset password error:', err);
    }
    setLoading(false);
  };

  const handleRotationChange = (index, field, value) => {
    const newRotations = [...rotatorData.rotations];
    newRotations[index][field] = value;
    setRotatorData({ ...rotatorData, rotations: newRotations });
  };

  const addRotation = () => {
    setRotatorData({ 
      ...rotatorData, 
      rotations: [...rotatorData.rotations, { school: '', region: '', zipCode: '', startDate: '', endDate: '', preferences: '' }] 
    });
  };

  const removeRotation = (index) => {
    const newRotations = rotatorData.rotations.filter((_, i) => i !== index);
    setRotatorData({ ...rotatorData, rotations: newRotations });
  };

  const handleSignup = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!formData.firstName || !formData.lastName) {
      setError('First and last name are required');
      setLoading(false);
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address (e.g., name@example.com)');
      setLoading(false);
      return;
    }

    if (!validateEduEmail(formData.email)) {
      setError('Please use your .edu email address to register');
      setLoading(false);
      return;
    }

    if (!formData.phoneNumber) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }

    if (!validatePhone(formData.phoneNumber)) {
      setError('Please enter a valid 10-digit US phone number');
      setLoading(false);
      return;
    }

    if (!formData.institution || formData.institution.trim().length === 0) {
      setError('School/Institution is required');
      setLoading(false);
      return;
    }

    if (!formData.gender) {
      setError('Please select your gender');
      setLoading(false);
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          username: formData.email, // Use email as username
          password: formData.password,
          userType: userType,
          phoneNumber: formData.phoneNumber,
          institution: formData.institution,
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if user is already registered and verified
        if (data.alreadyRegistered) {
          setError(data.error);
          setLoading(false);
          setTimeout(() => {
            // Redirect to login page
            navigateWithLoading('login');
          }, 1500);
          return;
        }
        
        setError(data.error || 'Registration failed');
        setLoading(false);
        return;
      }

      // New flow: No token returned, email verification required
      if (data.requiresVerification) {
        setVerificationEmail(data.email);
        setSuccess(data.message);
        setLoading(false);
        navigateWithLoading('verifyEmail');
      } else {
        // Old flow for backward compatibility (if verification is disabled)
        localStorage.setItem('token', data.token);
        setCurrentUser(data.user);
        setSuccess('Account created successfully!');
        
        setLoading(false);
        if (userType === 'host') {
          navigateWithLoading('hostProfile');
        } else {
          navigateWithLoading('rotatorProfile');
        }
      }
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Signup error:', err);
    }
  };

  const handleLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    if (!validateEmail(loginData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!loginData.password) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's an email verification error
        if (data.requiresVerification) {
          setVerificationEmail(data.email);
          setError(data.error);
          setLoading(false);
          // Don't return here - let the UI show the resend button
          return;
        }
        setError(data.error || 'Login failed');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      setCurrentUser(data.user);
      await checkUserProfiles(data.token);
      await fetchUserProfiles(data.token);
      setSuccess('Login successful!');
      
      setLoading(false);
      navigateWithLoading('dashboard');
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Login error:', err);
    }
  };

  const handleResendVerification = async (email) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to resend verification email');
        setLoading(false);
        return;
      }

      setSuccess(data.message);
      setLoading(false);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
      console.error('Resend verification error:', err);
    }
  };

  const handleCoordinatorLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/coordinator-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coordinatorLoginData)
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Coordinator login failed');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      setCurrentUser(data.user);
      await fetchCoordinatorDashboard(data.token);
      setSuccess('Login successful!');
      
      setLoading(false);
      navigateWithLoading('coordinatorDashboard');
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Coordinator login error:', err);
    }
  };

  const handleCalculateDistance = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    const { selectedHost, selectedRotator, selectedRotation } = distanceCalculator;

    if (!selectedHost || !selectedRotator || !selectedRotation) {
      setError('Please select both a host and a rotation');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/coordinator/calculate-distance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          hostZipCode: selectedHost.zip_code,
          rotationZipCode: selectedRotation.zipCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        setDistanceCalculator({
          ...distanceCalculator,
          distance: data.distance
        });
        setSuccess(`Distance: ${data.distance} miles between ${data.hostLocation} and ${data.rotationLocation}`);
      } else {
        setError(data.error || 'Failed to calculate distance');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Distance calculation error:', err);
    }
    setLoading(false);
  };

  const handlePasswordChange = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    if (!passwordChangeData.currentPassword || !passwordChangeData.newPassword || !passwordChangeData.confirmNewPassword) {
      setError('All password fields are required');
      setLoading(false);
      return;
    }

    if (passwordChangeData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmNewPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordChangeData.currentPassword,
          newPassword: passwordChangeData.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Password change failed');
        setLoading(false);
        return;
      }

      setSuccess('Password changed successfully!');
      setPasswordChangeData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setShowPasswordChange(false);
      setLoading(false);
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Password change error:', err);
    }
  };

  const handleDeleteAccount = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/${deleteConfirm.type}s/${deleteConfirm.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to delete account');
        setLoading(false);
        setDeleteConfirm({ show: false, type: null, id: null, name: null });
        return;
      }

      setSuccess(`${deleteConfirm.type === 'host' ? 'Host' : 'Rotator'} account deleted successfully`);
      
      // Refresh coordinator dashboard
      await fetchCoordinatorDashboard(localStorage.getItem('token'));
      
      setDeleteConfirm({ show: false, type: null, id: null, name: null });
      setLoading(false);
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      setDeleteConfirm({ show: false, type: null, id: null, name: null });
      console.error('Delete error:', err);
    }
  };

  const handleToggleVerified = async (rotatorId, currentStatus) => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/rotators/${rotatorId}/verified`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ verified: !currentStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to update verified status');
        setLoading(false);
        return;
      }

      // Refresh coordinator dashboard
      await fetchCoordinatorDashboard(localStorage.getItem('token'));
      setLoading(false);
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Toggle verified error:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
    setHostProfile(null);
    setRotatorProfile(null);
    setHasHostProfile(false);
    setHasRotatorProfile(false);
    setCoordinatorData(null);
    navigateWithLoading('landing');
  };

  const handleHostSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    if (!hostData.region || !hostData.zipCode || !hostData.description || !hostData.availableFrom || !hostData.availableTo) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }
    
    // Validate zip code
    if (!/^\d{5}$/.test(hostData.zipCode)) {
      setError('Please enter a valid 5-digit zip code');
      setLoading(false);
      return;
    }
    
    // Validate dates
    const startDate = new Date(hostData.availableFrom);
    const endDate = new Date(hostData.availableTo);
    
    if (endDate <= startDate) {
      setError('End date must be after start date');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = hasHostProfile ? `${API_URL}/hosts/me` : `${API_URL}/hosts`;
      const method = hasHostProfile ? 'PUT' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(hostData)
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save host profile');
        setLoading(false);
        return;
      }

      setSuccess(hasHostProfile ? 'Host profile updated!' : 'Host profile created!');
      await checkUserProfiles(token);
      await fetchUserProfiles(token);
      
      setLoading(false);
      navigateWithLoading('dashboard');
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Host submit error:', err);
    }
  };

  const handleRotatorSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
  
    const validRotations = rotatorData.rotations.filter(r => 
      r.school && r.region && r.zipCode && r.startDate && r.endDate
    );
    
    if (validRotations.length === 0) {
      setError('Please add at least one complete rotation with school, region, zip code, and dates');
      setLoading(false);
      return;
    }
    
    // Validate zip codes
    for (const rotation of validRotations) {
      if (!/^\d{5}$/.test(rotation.zipCode)) {
        setError(`Invalid zip code: ${rotation.zipCode}. Must be 5 digits.`);
        setLoading(false);
        return;
      }
    }
  
    try {
      const token = localStorage.getItem('token');
      const endpoint = hasRotatorProfile ? `${API_URL}/rotators/me` : `${API_URL}/rotators`;
      const method = hasRotatorProfile ? 'PUT' : 'POST';
      
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rotationDetails: validRotations,
          preferences: rotatorData.preferences,
          hospital: rotatorData.hospital
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        setError(data.error || 'Failed to save rotator profile');
        setLoading(false);
        return;
      }
  
      setSuccess(hasRotatorProfile ? 'Rotator profile updated!' : 'Rotator profile created! Coordinators have been notified.');
      await checkUserProfiles(token);
      await fetchUserProfiles(token);
      
      setLoading(false);
      navigateWithLoading('dashboard');
    } catch (err) {
      setError('Network error. Please ensure backend is running.');
      setLoading(false);
      console.error('Rotator submit error:', err);
    }
  };

  const loadHostProfileForEdit = () => {
    if (hostProfile) {
      setHostData({
        city: hostProfile.city || '',
        region: hostProfile.region || '',
        zipCode: hostProfile.zip_code || '',
        address: hostProfile.address || '',
        description: hostProfile.description,
        amenities: hostProfile.amenities || '',
        availability: hostProfile.availability || '',
        availableFrom: hostProfile.available_from?.split('T')[0] || '',
        availableTo: hostProfile.available_to?.split('T')[0] || '',
        visibleToStudents: hostProfile.visible_to_students,
        displayPublicly: hostProfile.display_publicly
      });
      navigateWithLoading('hostProfile');
    }
  };

  const loadRotatorProfileForEdit = () => {
    if (rotatorProfile) {
      // Convert rotation dates to proper format for date inputs
      const formattedRotations = rotatorProfile.rotation_details?.map(rotation => ({
        ...rotation,
        startDate: rotation.startDate ? rotation.startDate.split('T')[0] : '',
        endDate: rotation.endDate ? rotation.endDate.split('T')[0] : ''
      })) || [{ school: '', region: '', zipCode: '', startDate: '', endDate: '', preferences: '' }];

      setRotatorData({
        rotations: formattedRotations,
        preferences: rotatorProfile.preferences || '',
        hospital: rotatorProfile.hospital || ''
      });
      navigateWithLoading('rotatorProfile');
    }
  };

  const navigateToBrowseHosts = async (rotation) => {
    setSelectedRotation(rotation);
    
    // Get city name from zip code using our database
    try {
      const response = await fetch(`${API_URL}/zip/${rotation.zipCode}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedCity(`${data.city}, ${data.state}`);
      } else {
        // Fallback to zip code if lookup fails
        setSelectedCity(`ZIP ${rotation.zipCode}`);
      }
    } catch (error) {
      console.error('Error fetching city from zip code:', error);
      // Fallback to zip code if API fails
      setSelectedCity(`ZIP ${rotation.zipCode}`);
    }
    
    navigateWithLoading('browseHosts');
  };

  const renderTOS = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 my-8">
        <h2 className="text-3xl font-bold mb-4" style={{color: '#004B87'}}>ClerkshipConnect Terms of Service</h2>
        <div className="space-y-4 text-sm text-gray-700">
          <p className="text-gray-500">Last Updated: January 2026</p>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>1. Acceptance of Terms</h3>
            <p className="mb-2">By accessing or using ClerkshipConnect ("the Service," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.</p>
            <p>ClerkshipConnect is operated by a Michigan limited liability company.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>2. Description of Service</h3>
            <p className="mb-2">ClerkshipConnect is an online platform that facilitates connections between medical students seeking temporary housing during clerkships and individuals offering housing ("Hosts"). We provide a communication and listing platform only. We are NOT:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>A real estate broker or property manager</li>
              <li>A party to any housing agreement or lease</li>
              <li>Responsible for screening, vetting, or verifying users or properties</li>
              <li>A guarantor of any housing arrangement</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>3. Eligibility</h3>
            <p>You must be at least 18 years old to use this Service. By using the Service, you represent and warrant that you meet this requirement.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>4. User Account and Responsibilities</h3>
            <p className="font-semibold mb-1">a) Account Creation:</p>
            <p className="mb-2">You must provide accurate, current, and complete information during registration and maintain the accuracy of this information.</p>
            <p className="font-semibold mb-1">b) Account Security:</p>
            <p className="mb-2">You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.</p>
            <p className="font-semibold mb-1">c) Prohibited Conduct:</p>
            <p className="mb-1">You agree NOT to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Provide false or misleading information</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Service for any illegal purpose</li>
              <li>Harass, threaten, or harm other users</li>
              <li>Post fraudulent listings or engage in scams</li>
              <li>Attempt to circumvent any security features</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>5. Housing Arrangements</h3>
            <p className="font-semibold mb-1">a) Direct Agreements:</p>
            <p className="mb-2">All housing arrangements, including terms, conditions, payments, deposits, and durations, are made directly between users. ClerkshipConnect is not a party to these agreements.</p>
            <p className="font-semibold mb-1">b) No Verification:</p>
            <p className="mb-2">We do not verify the identity, credentials, or representations of any user. We do not inspect properties or confirm the accuracy of listings.</p>
            <p className="font-semibold mb-1">c) User Due Diligence:</p>
            <p className="mb-1">You are solely responsible for:</p>
            <ul className="list-disc ml-6 space-y-1 mb-2">
              <li>Evaluating potential housing arrangements</li>
              <li>Conducting background checks or verifications</li>
              <li>Inspecting properties before commitment</li>
              <li>Reviewing and understanding any lease or agreement</li>
              <li>Ensuring compliance with local housing laws</li>
              <li>Resolving any disputes with other users</li>
            </ul>
            <p className="font-semibold mb-1">d) Payments:</p>
            <p>Any payments for housing are made directly between users. ClerkshipConnect does not process, hold, or have any responsibility for financial transactions between users.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>6. Privacy and Data Use</h3>
            <p className="mb-2">Your personal information will be collected and used in accordance with our Privacy Policy. Your information will be used solely to facilitate housing connections. We will not sell your information to third parties, but we may share necessary information with other users to facilitate connections.</p>
            <p>By using the Service, you consent to receive communications from us regarding your account and the Service.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>7. Disclaimers and Limitation of Liability</h3>
            <p className="font-bold mb-2 text-red-600">PLEASE READ THIS SECTION CAREFULLY</p>
            <p className="font-semibold mb-1">a) DISCLAIMER OF WARRANTIES:</p>
            <p className="mb-2">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT.</p>
            <p className="font-semibold mb-1">b) NO RESPONSIBILITY FOR USER CONDUCT:</p>
            <p className="mb-1">ClerkshipConnect does not assume any responsibility for:</p>
            <ul className="list-disc ml-6 space-y-1 mb-2">
              <li>The conduct of any user, whether online or offline</li>
              <li>The accuracy, completeness, or usefulness of any user-generated content</li>
              <li>The safety, quality, legality, or suitability of any housing arrangement</li>
              <li>Any disputes between users</li>
              <li>Criminal activity or fraud by users</li>
            </ul>
            <p className="font-semibold mb-1">c) LIMITATION OF LIABILITY:</p>
            <p className="mb-2">TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLERKSHIPCONNECT AND ITS OWNERS, OFFICERS, MEMBERS, EMPLOYEES, AGENTS, AFFILIATES, SUCCESSORS, AND ASSIGNS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, DEATH, PROPERTY DAMAGE, LOST PROFITS, HOUSING DISPUTES, THEFT, FRAUD, OR EMOTIONAL DISTRESS.</p>
            <p className="font-semibold mb-1">d) MAXIMUM LIABILITY CAP:</p>
            <p>IN NO EVENT SHALL CLERKSHIPCONNECT'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE EXCEED THE GREATER OF (i) THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY, OR (ii) ONE HUNDRED DOLLARS ($100).</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>8. Indemnification</h3>
            <p>You agree to indemnify, defend, and hold harmless ClerkshipConnect and its owners, officers, members, employees, agents, affiliates, successors, and assigns from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees and court costs) arising from or related to your use or misuse of the Service, your violation of these Terms, or any housing arrangement you enter into through the Service.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>9. Dispute Resolution and Arbitration</h3>
            <p className="font-semibold mb-1">a) Binding Arbitration:</p>
            <p className="mb-2">Any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved by binding arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, rather than in court.</p>
            <p className="font-semibold mb-1">b) CLASS ACTION WAIVER:</p>
            <p className="mb-2 font-bold text-red-600">YOU AND CLERKSHIPCONNECT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.</p>
            <p className="font-semibold mb-1">c) Opt-Out Right:</p>
            <p>You may opt out of this arbitration agreement by sending written notice within thirty (30) days of first accepting these Terms.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>10. Time Limit for Bringing Claims</h3>
            <p className="font-bold mb-2 text-red-600">YOU AGREE THAT ANY CLAIM OR CAUSE OF ACTION ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE OR THESE TERMS MUST BE FILED WITHIN ONE (1) YEAR AFTER SUCH CLAIM OR CAUSE OF ACTION AROSE. AFTER THIS ONE-YEAR PERIOD, ANY SUCH CLAIM OR CAUSE OF ACTION IS PERMANENTLY BARRED.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>11. Governing Law</h3>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of Michigan, without regard to its conflict of law provisions.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>12. Termination</h3>
            <p className="mb-2">We reserve the right to suspend or terminate your account and access to the Service, with or without notice, for violation of these Terms, fraudulent or illegal activity, or any reason at our sole discretion.</p>
            <p>Upon termination, your right to use the Service will immediately cease. Sections that by their nature should survive termination shall survive, including but not limited to Disclaimers, Limitation of Liability, Indemnification, and Dispute Resolution.</p>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>13. Modifications to Terms</h3>
            <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Service with a new "Last Updated" date. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms.</p>
          </div>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <h3 className="font-bold text-lg mb-2" style={{color: '#004B87'}}>User Acknowledgment</h3>
            <p className="mb-2 font-semibold">By creating an account and using ClerkshipConnect, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. Specifically, you acknowledge and agree:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>ClerkshipConnect is a connection platform only and does not provide housing or verify users</li>
              <li>You are solely responsible for your housing arrangements and due diligence</li>
              <li>You agree to resolve disputes through arbitration, not court</li>
              <li>You waive your right to participate in class action lawsuits</li>
              <li>You have only one (1) year to bring any claims against ClerkshipConnect</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-lg mb-2" style={{color: '#5DBEA3'}}>14. Contact Information</h3>
            <p>If you have questions about these Terms, please contact us at: info.clerkshipconnect@gmail.com</p>
          </div>
        </div>
        
        <div className="flex gap-4 mt-6 pt-4 border-t">
          <button
            onClick={() => setShowTOS(false)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setAcceptedTOS(true);
              setShowTOS(false);
            }}
            className="flex-1 px-4 py-3 text-white rounded-lg hover:opacity-90 transition-all font-semibold"
            style={{backgroundColor: '#004B87'}}
          >
            I Accept These Terms
          </button>
        </div>
      </div>
    </div>
  );

  const DashboardButton = () => (
    currentUser && (
      <div className="fixed top-4 right-4 z-40">
        <button
          onClick={() => navigateWithLoading('dashboard')}
          className="text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:opacity-90 transition-all"
          style={{backgroundColor: '#004B87'}}
        >
          <Home className="w-4 h-4" />
          Dashboard
        </button>
      </div>
    )
  );

  if (loading && view === 'landing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{borderColor: '#004B87'}}></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto px-4 py-8 sm:py-16">
          {/* Logo and Header */}
          <div className="text-center mb-6 sm:mb-10">
            <img 
              src="/logo.png" 
              alt="ClerkshipConnect Logo" 
              className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 mx-auto mb-4 sm:mb-6"
            />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4" style={{color: '#004B87'}}>
              ClerkshipConnect
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">Connecting Medical Students with Housing Hosts</p>
          </div>

          {/* Mission and Vision */}
          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 space-y-6">
            {/* Mission Statement */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border-2" style={{borderColor: '#5DBEA3'}}>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center" style={{color: '#004B87'}}>Mission Statement</h2>
              <div className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                <p className="mb-4">
                  <span className="font-semibold" style={{color: '#5DBEA3'}}>Away rotations shouldn't break the bank.</span>
                </p>
                <p className="mb-4">
                  ClerkshipConnect matches medical students seeking housing during away clinical rotations with other healthcare community members offering low- to no-cost accommodations.
                </p>
                <p>
                  We're building a supportive community that makes medical education more affordable and accessible for everyone.
                </p>
              </div>
            </div>

            {/* Vision Statement */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border-2" style={{borderColor: '#004B87'}}>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center" style={{color: '#5DBEA3'}}>Vision Statement</h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed text-center">
                Medical students seeking away rotations will never be forced to spend exorbitant amounts of money or face them alone. No matter where they go.
              </p>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 border-2" style={{borderColor: '#5DBEA3'}}>
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center" style={{color: '#004B87'}}>How It Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Step 1 */}
                <div className="text-center">
                  <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3" style={{backgroundColor: '#E0F2F7'}}>
                    <Edit className="w-8 h-8" style={{color: '#004B87'}} />
                  </div>
                  <div className="mb-2 font-bold text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto text-sm" style={{backgroundColor: '#004B87'}}>
                    1
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Sign Up</h3>
                  <p className="text-sm text-gray-600">
                    Hosts and rotators create profiles with their information
                  </p>
                </div>

                {/* Step 2 */}
                <div className="text-center">
                  <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3" style={{backgroundColor: '#D1F2EB'}}>
                    <User className="w-8 h-8" style={{color: '#5DBEA3'}} />
                  </div>
                  <div className="mb-2 font-bold text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto text-sm" style={{backgroundColor: '#5DBEA3'}}>
                    2
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Match</h3>
                  <p className="text-sm text-gray-600">
                    Rotators are gender-matched with hosts within 50 miles of their rotation site
                  </p>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3" style={{backgroundColor: '#E0F2F7'}}>
                    <Mail className="w-8 h-8" style={{color: '#004B87'}} />
                  </div>
                  <div className="mb-2 font-bold text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto text-sm" style={{backgroundColor: '#004B87'}}>
                    3
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Connect</h3>
                  <p className="text-sm text-gray-600">
                    Contact information is provided to rotators to reach out to hosts
                  </p>
                </div>

                {/* Step 4 */}
                <div className="text-center">
                  <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3" style={{backgroundColor: '#D1F2EB'}}>
                    <CheckCircle className="w-8 h-8" style={{color: '#5DBEA3'}} />
                  </div>
                  <div className="mb-2 font-bold text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto text-sm" style={{backgroundColor: '#5DBEA3'}}>
                    4
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Agree</h3>
                  <p className="text-sm text-gray-600">
                    Both parties communicate directly to finalize their housing arrangement
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto mb-6 sm:mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-shadow">
              <div className="rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6 mx-auto" style={{backgroundColor: '#E0F2F7'}}>
                <User className="w-7 h-7 sm:w-8 sm:h-8" style={{color: '#004B87'}} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">I'm a Rotator</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 text-center">Looking for housing during clinical rotations</p>
              <button
                onClick={() => {
                  setUserType('rotator');
                  navigateWithLoading('signup');
                }}
                className="w-full text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:opacity-90 text-sm sm:text-base"
                style={{background: 'linear-gradient(to right, #004B87, #003366)'}}
              >
                Sign Up as Rotator
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-shadow">
              <div className="rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6 mx-auto" style={{backgroundColor: '#D1F2EB'}}>
                <Home className="w-7 h-7 sm:w-8 sm:h-8" style={{color: '#5DBEA3'}} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">I'm a Host</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 text-center">Offering housing to medical students</p>
              <button
                onClick={() => {
                  setUserType('host');
                  navigateWithLoading('signup');
                }}
                className="w-full text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:opacity-90 text-sm sm:text-base"
                style={{background: 'linear-gradient(to right, #5DBEA3, #4AA896)'}}
              >
                Sign Up as Host
              </button>
            </div>
          </div>

          {/* Consolidated Login Button */}
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-sm sm:text-base text-gray-600 mb-2">Already have an account?</p>
            <button
              onClick={() => navigateWithLoading('login')}
              className="inline-flex items-center gap-2 px-6 py-2.5 border-2 rounded-lg font-semibold hover:bg-opacity-10 transition-colors text-sm sm:text-base"
              style={{borderColor: '#004B87', color: '#004B87'}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 75, 135, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              Login
            </button>
          </div>

          <div className="text-center space-y-3 px-4">
            <button
              onClick={() => navigateWithLoading('coordinatorLogin')}
              className="font-medium block mx-auto text-sm sm:text-base hover:opacity-80 transition-all"
              style={{color: '#004B87'}}
            >
              Coordinator Login →
            </button>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 items-center">
              <button
                onClick={() => navigateWithLoading('mission')}
                className="font-medium text-sm sm:text-base hover:opacity-80 transition-all"
                style={{color: '#5DBEA3'}}
              >
                Our Mission
              </button>
              <span className="text-gray-400">•</span>
              <button
                onClick={() => navigateWithLoading('contact')}
                className="font-medium text-sm sm:text-base hover:opacity-80 transition-all"
                style={{color: '#5DBEA3'}}
              >
                Contact Us
              </button>
            </div>
          </div>

          {/* Footer with Attribution */}
          <div className="max-w-4xl mx-auto mt-8 sm:mt-16 pt-6 sm:pt-8 border-t px-4" style={{borderColor: '#5DBEA3'}}>
            <p className="text-center text-xs sm:text-sm text-gray-600">
              Zip code data provided by{' '}
              <a 
                href="https://simplemaps.com/data/us-zips" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline"
                style={{color: '#004B87'}}
              >
                SimpleMaps
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <button onClick={() => navigateWithLoading('landing')} className="mb-4" style={{color: '#004B87'}}>
            ← Back
          </button>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h2>
          <p className="text-gray-600 mb-6">Login to your account</p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
              {verificationEmail && (
                <button
                  onClick={() => handleResendVerification(verificationEmail)}
                  className="mt-3 w-full text-white py-2 rounded-lg transition-colors text-sm font-medium"
                  style={{backgroundColor: '#004B87'}}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#003366'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#004B87'}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
            </div>
          )}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={loginData.email}
                  onChange={(e) => handleInputChange(e, setLoginData, loginData)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="your.email@med.school.edu"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={(e) => handleInputChange(e, setLoginData, loginData)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter password"
                />
              </div>
            </div>
            
            <button
              onClick={handleLogin}
              className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
              style={{background: 'linear-gradient(to right, #004B87, #003366)'}}
            >
              Login
            </button>

            <div className="text-center mt-4">
              <button
                onClick={() => navigateWithLoading('forgotPassword')}
                className="text-sm hover:opacity-80 transition-all"
                style={{color: '#5DBEA3'}}
              >
                Forgot Password?
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'forgotPassword') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <button onClick={() => navigateWithLoading('login')} className="mb-4" style={{color: '#004B87'}}>
            ← Back to Login
          </button>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Forgot Password</h2>
          <p className="text-gray-600 mb-6">Enter your email to receive a password reset code</p>
          
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="your.email@med.school.edu"
                  onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
            </div>
            
            <button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50"
              style={{background: loading ? '#9CA3AF' : 'linear-gradient(to right, #004B87, #003366)'}}
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'resetPassword') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <button onClick={() => navigateWithLoading('login')} className="mb-4" style={{color: '#004B87'}}>
            ← Back to Login
          </button>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Reset Password</h2>
          <p className="text-gray-600 mb-6">Enter the code sent to your email and your new password</p>
          
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reset Code</label>
              <input
                type="text"
                value={resetPasswordData.code}
                onChange={(e) => setResetPasswordData({...resetPasswordData, code: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
              />
              <p className="text-xs text-gray-500 mt-1 text-center">Enter the 6-digit code from your email</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({...resetPasswordData, newPassword: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter new password"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData({...resetPasswordData, confirmPassword: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Confirm new password"
                  onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
                />
              </div>
            </div>
            
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50"
              style={{background: loading ? '#9CA3AF' : 'linear-gradient(to right, #004B87, #003366)'}}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="text-center mt-4">
              <button
                onClick={() => navigateWithLoading('forgotPassword')}
                className="text-sm hover:opacity-80 transition-all"
                style={{color: '#5DBEA3'}}
              >
                Didn't receive a code? Request new one
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'verifyEmail') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#D1F2EB'}}>
            <Mail className="w-10 h-10" style={{color: '#5DBEA3'}} />
          </div>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Verify Your Email</h2>
          <p className="text-gray-600 mb-2">
            We've sent a 6-digit code to:
          </p>
          <p className="text-lg font-semibold mb-4" style={{color: '#004B87'}}>{verificationEmail}</p>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4 text-left">
            <p className="text-sm text-blue-800">
              <strong>📬 Important:</strong> Please check your spam/junk folder if you don't see the email. 
              It may take a few minutes for the code to arrive in your inbox.
            </p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-gray-700 mb-2">📧 <strong>Next steps:</strong></p>
            <ol className="text-sm text-gray-600 ml-4 space-y-1 list-decimal">
              <li>Check your inbox (and spam folder)</li>
              <li>Enter the 6-digit code below</li>
              <li>Click "Verify Email"</li>
            </ol>
          </div>

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2 justify-center">
              <CheckCircle className="w-5 h-5" />
              {success}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && verificationCode.length === 6) {
                  handleEmailVerification();
                }
              }}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border-2 rounded-lg focus:outline-none"
              style={{borderColor: '#004B87'}}
              onFocus={(e) => e.currentTarget.style.borderColor = '#5DBEA3'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#004B87'}
            />
            <p className="text-xs text-gray-500 mt-2">Enter the 6-digit code from your email</p>
          </div>
          
          <button
            onClick={handleEmailVerification}
            disabled={loading || verificationCode.length !== 6}
            className="w-full mb-3 text-white py-3 rounded-lg font-semibold transition-all disabled:bg-gray-400 disabled:cursor-not-allowed hover:opacity-90"
            style={{background: verificationCode.length === 6 ? 'linear-gradient(to right, #004B87, #003366)' : '#9CA3AF'}}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
          
          <button
            onClick={() => handleResendVerification(verificationEmail)}
            disabled={loading}
            className="w-full mb-3 border-2 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 hover:bg-opacity-5"
            style={{borderColor: '#5DBEA3', color: '#5DBEA3'}}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = 'rgba(93, 190, 163, 0.05)')}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {loading ? 'Sending...' : 'Resend Code'}
          </button>
          
          <button
            onClick={() => navigateWithLoading('login')}
            className="w-full text-gray-600 hover:text-gray-800 text-sm"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (view === 'coordinatorLogin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <button onClick={() => navigateWithLoading('landing')} className="mb-4" style={{color: '#004B87'}}>
            ← Back
          </button>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Coordinator Login</h2>
          <p className="text-gray-600 mb-6">Access coordinator dashboard</p>
          
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={coordinatorLoginData.email}
                  onChange={(e) => handleInputChange(e, setCoordinatorLoginData, coordinatorLoginData)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="info.clerkshipconnect@gmail.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={coordinatorLoginData.password}
                  onChange={(e) => handleInputChange(e, setCoordinatorLoginData, coordinatorLoginData)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter password"
                  onKeyPress={(e) => e.key === 'Enter' && handleCoordinatorLogin()}
                />
              </div>
            </div>
            
            <button
              onClick={handleCoordinatorLogin}
              className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
              style={{background: 'linear-gradient(to right, #004B87, #003366)'}}
            >
              Login as Coordinator
            </button>

            <div className="text-center mt-4">
              <button
                onClick={() => navigateWithLoading('forgotPassword')}
                className="text-sm hover:opacity-80 transition-all"
                style={{color: '#5DBEA3'}}
              >
                Forgot Password?
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'signup') {
    return (
      <>
        {showTOS && renderTOS()}
        <DashboardButton />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
            <button onClick={() => navigateWithLoading('landing')} className="mb-4" style={{color: '#004B87'}}>
              ← Back
            </button>
            
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Create Account</h2>
            <p className="text-gray-600 mb-6">Sign up as a {userType}</p>
            
            {/* Important Notice for Non-US Medical Students - Rotators Only */}
            {userType === 'rotator' && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <h3 className="text-base font-semibold text-blue-900 mb-2">Important Notice For Non-US Medical Students</h3>
                <p className="text-sm text-blue-800 mb-2">
                  ClerkshipConnect does not currently have the capacity to serve international medical students. We sincerely apologize for any inconvenience this may cause.
                </p>
                <p className="text-sm text-blue-800 mb-2">
                  As we launch this initiative, we are focusing our resources on supporting students enrolled in US medical schools. Expanding our services at this time would compromise our ability to effectively serve our current user base.
                </p>
                <p className="text-sm text-blue-800">
                  To maintain the integrity of our platform, we will be implementing identity verification to confirm that all users are affiliated with US medical schools.
                </p>
                <p className="text-sm text-blue-800 mt-2 font-medium">
                  We appreciate your understanding and wish you the best of luck.
                </p>
              </div>
            )}
            
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange(e, setFormData, formData)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange(e, setFormData, formData)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="your.email@med.school.edu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="(123) 456-7890"
                />
                <p className="text-xs text-gray-500 mt-1">10-digit US phone number</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School/Institution *</label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Your medical school"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="At least 6 characters"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange(e, setFormData, formData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Confirm password"
                />
              </div>
              
              {!acceptedTOS ? (
                <button
                  onClick={() => setShowTOS(true)}
                  className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
                  style={{background: 'linear-gradient(to right, #004B87, #003366)'}}
                >
                  Review Terms & Create Account
                </button>
              ) : (
                <button
                  onClick={handleSignup}
                  className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
                  style={{background: 'linear-gradient(to right, #004B87, #003366)'}}
                >
                  Create Account
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (view === 'hostProfile') {
    return (
      <>
        <DashboardButton />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-4">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-gray-800">{hasHostProfile ? 'Edit' : 'Create'} Host Profile</h2>
              <button onClick={handleLogout} className="text-red-600 flex items-center gap-2">
                <LogOut className="w-5 h-5" />Logout
              </button>
            </div>
            
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4"><CheckCircle className="w-5 h-5 inline mr-2" />{success}</div>}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Region (Approximately) *</label>
                <select
                  name="region"
                  value={hostData.region}
                  onChange={(e) => handleInputChange(e, setHostData, hostData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select a region...</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={hostData.zipCode}
                  onChange={(e) => handleInputChange(e, setHostData, hostData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 10001"
                  maxLength="5"
                />
                <p className="text-xs text-gray-500 mt-1">5-digit US zip code</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available From *</label>
                  <input
                    type="date"
                    name="availableFrom"
                    value={hostData.availableFrom}
                    onChange={(e) => handleInputChange(e, setHostData, hostData)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Available To *</label>
                  <input
                    type="date"
                    name="availableTo"
                    value={hostData.availableTo}
                    onChange={(e) => handleInputChange(e, setHostData, hostData)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  rows="4"
                  value={hostData.description}
                  onChange={(e) => handleInputChange(e, setHostData, hostData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe your space"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <input
                  type="text"
                  name="amenities"
                  value={hostData.amenities}
                  onChange={(e) => handleInputChange(e, setHostData, hostData)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="WiFi, parking, etc"
                />
              </div>

              <div className="rounded-lg p-4" style={{borderColor: '#5DBEA3', backgroundColor: '#D1F2EB', borderWidth: '1px'}}>
                <label className="block text-sm font-medium text-gray-700 mb-3">Listing Visibility</label>
                
                <div className="flex items-start gap-2 mb-3">
                  <input
                    type="checkbox"
                    name="displayPublicly"
                    checked={hostData.displayPublicly}
                    onChange={(e) => setHostData({ ...hostData, displayPublicly: e.target.checked })}
                    className="mt-1 w-4 h-4"
                  />
                  <div>
                    <label className="text-sm text-gray-700 font-medium">Display my listing publicly to students</label>
                    <p className="text-xs text-gray-600">Students searching for housing will be able to see and contact you directly</p>
                  </div>
                </div>

                <div className="rounded p-3 mt-3" style={{backgroundColor: '#E0F7F1'}}>
                  <p className="text-xs text-gray-700">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Your profile is automatically visible to coordinators to help match you with students
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleHostSubmit}
                className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
                style={{background: 'linear-gradient(to right, #5DBEA3, #4AA896)'}}
              >
                {hasHostProfile ? 'Update Profile' : 'Complete Profile'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (view === 'rotatorProfile') {
    return (
      <>
        <DashboardButton />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-2 sm:p-4">
          <div className="max-w-2xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 mt-4 sm:mt-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">{hasRotatorProfile ? 'Edit' : 'Create'} Rotator Profile</h2>
              <button onClick={handleLogout} className="text-red-600 flex items-center justify-center gap-2 text-sm sm:text-base">
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />Logout
              </button>
            </div>
            
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm"><CheckCircle className="w-5 h-5 inline mr-2" />{success}</div>}
            
            <div className="space-y-4 sm:space-y-6">
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 mb-3">
                  <label className="block text-sm font-medium text-gray-700">Rotation Details *</label>
                  <button
                    onClick={addRotation}
                    className="text-sm text-white px-3 py-1.5 rounded-lg w-full sm:w-auto hover:opacity-90 transition-all"
                    style={{backgroundColor: '#004B87'}}
                  >
                    + Add Another Rotation
                  </button>
                </div>
                
                {rotatorData.rotations.map((rotation, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 mb-3">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-700 text-sm sm:text-base">Rotation {index + 1}</h4>
                      {rotatorData.rotations.length > 1 && (
                        <button
                          onClick={() => removeRotation(index)}
                          className="text-red-600 text-xs sm:text-sm hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">School/Hospital *</label>
                        <input
                          type="text"
                          value={rotation.school}
                          onChange={(e) => handleRotationChange(index, 'school', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                          placeholder="e.g., Mount Sinai Hospital"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Region (Approximately) *</label>
                        <select
                          value={rotation.region}
                          onChange={(e) => handleRotationChange(index, 'region', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                        >
                          <option value="">Select a region...</option>
                          {regions.map((region) => (
                            <option key={region} value={region}>{region}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code *</label>
                        <input
                          type="text"
                          value={rotation.zipCode}
                          onChange={(e) => handleRotationChange(index, 'zipCode', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                          placeholder="e.g., 10001"
                          maxLength="5"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                          <input
                            type="date"
                            value={rotation.startDate}
                            onChange={(e) => handleRotationChange(index, 'startDate', e.target.value)}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                          <input
                            type="date"
                            value={rotation.endDate}
                            onChange={(e) => handleRotationChange(index, 'endDate', e.target.value)}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preferences for this rotation (Optional)</label>
                        <textarea
                          rows="2"
                          value={rotation.preferences}
                          onChange={(e) => handleRotationChange(index, 'preferences', e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                          placeholder="Any specific preferences for this rotation..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={handleRotatorSubmit}
                disabled={loading}
                className="w-full text-white py-3 rounded-lg font-semibold text-sm sm:text-base disabled:opacity-50 hover:opacity-90 transition-all"
                style={{background: loading ? '#9CA3AF' : 'linear-gradient(to right, #004B87, #003366)'}}
              >
                {loading ? 'Saving...' : (hasRotatorProfile ? 'Update Profile' : 'Complete Profile')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (view === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-2 sm:p-4">
        <div className="max-w-7xl mx-auto mt-4 sm:mt-8">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Welcome, {currentUser?.firstName} {currentUser?.lastName}!</h2>
                <p className="text-sm sm:text-base text-gray-600">Your dashboard</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button 
                  onClick={() => navigateWithLoading('contact')} 
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm sm:text-base text-white hover:opacity-90 transition-all"
                  style={{backgroundColor: '#5DBEA3'}}
                >
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5" />Contact Us
                </button>
                <button onClick={handleLogout} className="flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm sm:text-base">
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />Logout
                </button>
              </div>
            </div>
  
            {/* User Info Section */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Your Information</h3>
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="flex items-center justify-center gap-2 px-3 py-1.5 text-white rounded-lg text-sm w-full sm:w-auto hover:opacity-90 transition-all"
                  style={{backgroundColor: '#004B87'}}
                >
                  <Lock className="w-4 h-4" />
                  Change Password
                </button>
              </div>

              {showPasswordChange && (
                <div className="bg-white border rounded-lg p-4 mb-4" style={{borderColor: '#5DBEA3'}}>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Change Password</h4>
                  {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-3 text-sm">{error}</div>}
                  {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-3 flex items-center gap-2 text-sm"><CheckCircle className="w-5 h-5" />{success}</div>}
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordChangeData.currentPassword}
                        onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={passwordChangeData.newPassword}
                        onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="At least 6 characters"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmNewPassword"
                        value={passwordChangeData.confirmNewPassword}
                        onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Confirm new password"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={handlePasswordChange}
                        className="px-4 py-2 text-white rounded-lg text-sm hover:opacity-90 transition-all"
                        style={{backgroundColor: '#004B87'}}
                      >
                        Update Password
                      </button>
                      <button
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordChangeData({
                            currentPassword: '',
                            newPassword: '',
                            confirmNewPassword: ''
                          });
                          setError('');
                          setSuccess('');
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Email</p>
                  <p className="font-medium">{currentUser?.email}</p>
                </div>
                <div>
                  <p className="text-gray-600">Phone</p>
                  <p className="font-medium">{currentUser?.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-gray-600">Gender</p>
                  <p className="font-medium capitalize">{currentUser?.gender}</p>
                </div>
                <div>
                  <p className="text-gray-600">Institution</p>
                  <p className="font-medium">{currentUser?.institution}</p>
                </div>
              </div>
            </div>
  
            {/* Side-by-Side Profiles Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              {/* Rotator Profile Card */}
              <div className="rounded-lg p-4 sm:p-6" style={{backgroundColor: '#E0F2F7', borderColor: '#004B87', borderWidth: '1px'}}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Rotator Profile</h3>
                  {hasRotatorProfile && (
                    <button 
                      onClick={loadRotatorProfileForEdit}
                      className="flex items-center justify-center gap-2 px-3 py-1.5 text-white rounded-lg text-sm w-full sm:w-auto hover:opacity-90 transition-all"
                      style={{backgroundColor: '#004B87'}}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                </div>
                
                {hasRotatorProfile && rotatorProfile ? (
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-600 font-semibold mb-2">Your Rotations:</p>
                      {rotatorProfile.rotation_details && rotatorProfile.rotation_details.length > 0 ? (
                        rotatorProfile.rotation_details.map((rotation, index) => (
                          <div key={index} className="bg-white rounded-lg p-3 mb-2" style={{borderColor: '#E0F2F7', borderWidth: '1px'}}>
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 flex-shrink-0" style={{color: '#004B87'}} />
                              <p className="font-semibold text-gray-800 text-sm">{rotation.school}</p>
                            </div>
                            <div className="flex items-start gap-2 text-gray-600 ml-6">
                              <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <p className="text-xs">
                                {formatDateDisplay(rotation.startDate)} - {formatDateDisplay(rotation.endDate)}
                              </p>
                            </div>
                            {rotation.preferences && (
                              <p className="text-xs text-gray-600 mt-1 ml-6"><strong>Preferences:</strong> {rotation.preferences}</p>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500">No rotations added yet</p>
                      )}
                    </div>
                    
                    {/* Browse Hosts Button */}
                    {rotatorProfile.rotation_details && rotatorProfile.rotation_details.length > 0 && (
                      <div className="pt-3 border-t" style={{borderColor: '#5DBEA3'}}>
                        <p className="text-xs text-gray-600 mb-2">Browse available hosts by rotation:</p>
                        <div className="flex flex-wrap gap-2">
                          {rotatorProfile.rotation_details.map((rotation, index) => (
                            <button
                              key={index}
                              onClick={() => navigateToBrowseHosts(rotation)}
                              className="px-3 py-1.5 text-white rounded-lg text-xs font-medium hover:opacity-90 transition-all"
                              style={{backgroundColor: '#004B87'}}
                            >
                              {rotation.school}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <User className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm sm:text-base text-gray-600 mb-4">You don't have a rotation profile yet</p>
                    <button
                      onClick={() => navigateWithLoading('rotatorProfile')}
                      className="px-6 py-2 text-white rounded-lg text-sm sm:text-base w-full sm:w-auto hover:opacity-90 transition-all"
                      style={{backgroundColor: '#004B87'}}
                    >
                      Create Rotation Profile
                    </button>
                  </div>
                )}
              </div>
  
              {/* Host Profile Card */}
              <div className="rounded-lg p-4 sm:p-6" style={{backgroundColor: '#D1F2EB', borderColor: '#5DBEA3', borderWidth: '1px'}}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Host Profile</h3>
                  {hasHostProfile && (
                    <button 
                      onClick={loadHostProfileForEdit}
                      className="flex items-center justify-center gap-2 px-3 py-1.5 text-white rounded-lg text-sm w-full sm:w-auto hover:opacity-90 transition-all"
                      style={{backgroundColor: '#5DBEA3'}}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                </div>
                
                {hasHostProfile && hostProfile ? (
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-600">Region</p>
                      <p className="font-medium">{hostProfile.region || hostProfile.city}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Available</p>
                      <p className="font-medium text-xs sm:text-sm">
                        {formatDateDisplay(hostProfile.available_from)} - {formatDateDisplay(hostProfile.available_to)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Description</p>
                      <p className="font-medium text-xs">{hostProfile.description}</p>
                    </div>
                    {hostProfile.amenities && (
                      <div>
                        <p className="text-gray-600">Amenities</p>
                        <p className="font-medium text-xs">{hostProfile.amenities}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-600">Public Listing</p>
                      <p className="font-medium text-xs sm:text-sm">{hostProfile.display_publicly ? 'Yes' : 'Coordinators Only'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Home className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm sm:text-base text-gray-600 mb-4">You don't have a host profile yet</p>
                    <button
                      onClick={() => navigateWithLoading('hostProfile')}
                      className="px-6 py-2 text-white rounded-lg text-sm sm:text-base w-full sm:w-auto hover:opacity-90 transition-all"
                      style={{backgroundColor: '#5DBEA3'}}
                    >
                      Create Host Profile
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'browseHosts') {
    return (
      <>
        <DashboardButton />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <div className="max-w-6xl mx-auto mt-8">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => navigateWithLoading('dashboard')}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                >
                  ← Back to Dashboard
                </button>
                <div className="flex-1">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
                    Available Hosts in {selectedCity || 'Loading...'}
                  </h2>
                  {selectedRotation && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <p className="text-sm sm:text-base text-gray-600">For rotation at:</p>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        <MapPin className="w-4 h-4" />
                        {selectedRotation.school}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">
                        {formatDateDisplay(selectedRotation.startDate)} - {formatDateDisplay(selectedRotation.endDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
  
              {availableHosts && availableHosts.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {availableHosts
                    .filter(host => selectedRotation ? host.searchZipCode === selectedRotation.zipCode : true)
                    .map((host) => (
                    <div key={host.id} className="bg-blue-50 border border-blue-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-semibold text-gray-800">{host.first_name} {host.last_name}</p>
                            <p className="text-xs text-green-600 font-medium">{host.distance} miles away</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#5DBEA3'}} />
                          <div>
                            <p className="text-sm text-gray-600">Location</p>
                            <p className="font-medium text-gray-800 text-sm">
                              {host.host_city}, {host.host_state} {host.zip_code}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <User className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#5DBEA3'}} />
                          <div>
                            <p className="text-sm text-gray-600">Gender</p>
                            <p className="font-medium text-gray-800 capitalize text-sm">{host.gender}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#5DBEA3'}} />
                          <div>
                            <p className="text-sm text-gray-600">Available Dates</p>
                            <p className="font-medium text-gray-800 text-sm">
                              {formatDateDisplay(host.available_from)} - {formatDateDisplay(host.available_to)}
                            </p>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-200">
                          <p className="text-sm text-gray-600 mb-1">Description</p>
                          <p className="text-sm text-gray-700">{host.description}</p>
                        </div>

                        {host.amenities && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Amenities</p>
                            <p className="text-sm text-gray-700">{host.amenities}</p>
                          </div>
                        )}
                      </div>

                      <div className="pt-4 border-t border-gray-200 mt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-3">Contact Information</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4" style={{color: '#5DBEA3'}} />
                            <a 
                              href={`mailto:${host.email}`} 
                              className="hover:underline"
                              style={{color: '#5DBEA3'}}
                            >
                              {host.email}
                            </a>
                          </div>
                          {host.phone_number && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4" style={{color: '#5DBEA3'}} />
                              <span className="text-gray-700 font-medium">{host.phone_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : availableHosts && selectedRotation && availableHosts.filter(h => h.searchZipCode === selectedRotation.zipCode).length === 0 ? (
                <div className="text-center py-16">
                  <Eye className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Available Hosts</h3>
                  <p className="text-gray-600 mb-1">No hosts are currently available within 50 miles of {selectedCity}.</p>
                  <p className="text-gray-500 text-sm">Coordinators have been notified of your request and will help you find housing.</p>
                </div>
              ) : (
                <div className="text-center py-16">
                  <Eye className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Available Hosts</h3>
                  <p className="text-gray-600 mb-1">No hosts are currently available.</p>
                  <p className="text-gray-500 text-sm">Coordinators have been notified of your request and will help you find housing.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  if (view === 'coordinatorDashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-4">
        {/* Delete Confirmation Dialog */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the account for <strong>{deleteConfirm.name}</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm({ show: false, type: null, id: null, name: null })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto mt-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Header with City Name Prominent */}
            <div className="text-white rounded-xl p-6 mb-6" style={{background: 'linear-gradient(to right, #004B87, #003366)'}}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-4xl font-bold mb-2">{coordinatorData?.city}</h2>
                  <p className="text-lg" style={{color: '#D1F2EB'}}>Coordinator Dashboard</p>
                  <p className="text-sm mt-1" style={{color: '#E0F2F7'}}>
                    Managing {coordinatorData?.rotators?.length || 0} rotators and {coordinatorData?.hosts?.length || 0} hosts
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCoordinatorPasswordChange(!showCoordinatorPasswordChange)}
                    className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg backdrop-blur-sm"
                  >
                    <Lock className="w-5 h-5" />
                    Change Password
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg backdrop-blur-sm"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </div>
              </div>
            </div>

            {/* Error and Success Messages */}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2"><CheckCircle className="w-5 h-5" />{success}</div>}

            {/* Password Change Section */}
            {showCoordinatorPasswordChange && (
              <div className="rounded-lg p-6 mb-6" style={{backgroundColor: '#D1F2EB', borderColor: '#5DBEA3', borderWidth: '1px'}}>
                <h4 className="font-semibold text-gray-800 mb-4 text-lg">Change Password</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordChangeData.currentPassword}
                      onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordChangeData.newPassword}
                      onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmNewPassword"
                      value={passwordChangeData.confirmNewPassword}
                      onChange={(e) => handleInputChange(e, setPasswordChangeData, passwordChangeData)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handlePasswordChange}
                      className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-all"
                      style={{backgroundColor: '#5DBEA3'}}
                    >
                      Update Password
                    </button>
                    <button
                      onClick={() => {
                        setShowCoordinatorPasswordChange(false);
                        setPasswordChangeData({
                          currentPassword: '',
                          newPassword: '',
                          confirmNewPassword: ''
                        });
                        setError('');
                        setSuccess('');
                      }}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Distance Calculator Section (Admin Only) */}
            {coordinatorData?.city === 'All' && (
              <div className="mb-6">
                <button
                  onClick={() => setDistanceCalculator({ ...distanceCalculator, show: !distanceCalculator.show })}
                  className="w-full flex items-center justify-between p-4 rounded-lg hover:opacity-90 transition-all"
                  style={{backgroundColor: '#004B87', color: 'white'}}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span className="font-semibold">Distance Calculator</span>
                  </div>
                  <span>{distanceCalculator.show ? '▼' : '▶'}</span>
                </button>

                {distanceCalculator.show && (
                  <div className="rounded-lg p-6 mt-2" style={{backgroundColor: '#E0F2F7', borderColor: '#004B87', borderWidth: '1px'}}>
                    <p className="text-gray-700 mb-4">Calculate the distance between a host location and a rotation site.</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Select Host */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Host</label>
                        <select
                          value={distanceCalculator.selectedHost?.id || ''}
                          onChange={(e) => {
                            const host = coordinatorData.hosts.find(h => h.id === parseInt(e.target.value));
                            setDistanceCalculator({ ...distanceCalculator, selectedHost: host, distance: null });
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">-- Select a host --</option>
                          {coordinatorData?.hosts?.map(host => (
                            <option key={host.id} value={host.id}>
                              {host.first_name} {host.last_name} - {host.host_city || host.city || 'City'}, {host.host_state || host.state || 'State'}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Select Rotator */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Rotator</label>
                        <select
                          value={distanceCalculator.selectedRotator?.id || ''}
                          onChange={(e) => {
                            const rotator = coordinatorData.rotators.find(r => r.id === parseInt(e.target.value));
                            setDistanceCalculator({ 
                              ...distanceCalculator, 
                              selectedRotator: rotator, 
                              selectedRotation: null,
                              distance: null 
                            });
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">-- Select a rotator --</option>
                          {coordinatorData?.rotators?.map(rotator => (
                            <option key={rotator.id} value={rotator.id}>
                              {rotator.first_name} {rotator.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Select Rotation (appears after rotator is selected) */}
                    {distanceCalculator.selectedRotator && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Rotation Site</label>
                        <select
                          value={distanceCalculator.selectedRotation ? JSON.stringify(distanceCalculator.selectedRotation) : ''}
                          onChange={(e) => {
                            const rotation = e.target.value ? JSON.parse(e.target.value) : null;
                            setDistanceCalculator({ ...distanceCalculator, selectedRotation: rotation, distance: null });
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">-- Select a rotation site --</option>
                          {distanceCalculator.selectedRotator.rotation_details?.map((rotation, index) => (
                            <option key={index} value={JSON.stringify(rotation)}>
                              {rotation.school} - {rotation.city} ({formatDateDisplay(rotation.startDate)} to {formatDateDisplay(rotation.endDate)})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Calculate Button */}
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={handleCalculateDistance}
                        disabled={loading || !distanceCalculator.selectedHost || !distanceCalculator.selectedRotation}
                        className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                        style={{backgroundColor: '#5DBEA3'}}
                      >
                        {loading ? 'Calculating...' : 'Calculate Distance'}
                      </button>
                      <button
                        onClick={() => setDistanceCalculator({
                          selectedHost: null,
                          selectedRotator: null,
                          selectedRotation: null,
                          distance: null,
                          show: true
                        })}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Clear
                      </button>
                    </div>

                    {/* Distance Result */}
                    {distanceCalculator.distance !== null && (
                      <div className="mt-4 p-4 rounded-lg" style={{backgroundColor: '#D1F2EB', borderColor: '#5DBEA3', borderWidth: '1px'}}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5" style={{color: '#5DBEA3'}} />
                          <p className="text-lg font-semibold" style={{color: '#004B87'}}>
                            Distance: {distanceCalculator.distance} miles
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          From: {distanceCalculator.selectedHost.first_name} {distanceCalculator.selectedHost.last_name} ({distanceCalculator.selectedHost.host_city || distanceCalculator.selectedHost.city}, {distanceCalculator.selectedHost.host_state || distanceCalculator.selectedHost.state})
                        </p>
                        <p className="text-sm text-gray-600">
                          To: {distanceCalculator.selectedRotation.school} ({distanceCalculator.selectedRotation.city})
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ROTATORS SECTION - NOW FIRST */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Rotators ({coordinatorData?.rotators?.length || 0})
                </h3>
              </div>
              {coordinatorData?.rotators && coordinatorData.rotators.length > 0 ? (
                <div className="space-y-4">
                  {coordinatorData.rotators.map((rotator) => (
                    <div key={rotator.id} className="rounded-lg p-6" style={{backgroundColor: '#E0F2F7', borderColor: '#004B87', borderWidth: '1px'}}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-800">
                              {rotator.first_name} {rotator.last_name}
                            </h4>
                            {rotator.verified && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                ✓ Verified
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1 text-sm">
                            <p className="text-gray-600">
                              <Mail className="w-4 h-4 inline mr-2" />
                              {rotator.email}
                            </p>
                            <p className="text-gray-600">
                              <Phone className="w-4 h-4 inline mr-2" />
                              {rotator.phone_number}
                            </p>
                            <p className="text-gray-600">
                              <Building className="w-4 h-4 inline mr-2" />
                              {rotator.institution}
                            </p>
                            <p className="text-gray-600">
                              <User className="w-4 h-4 inline mr-2" />
                              Gender: <span className="capitalize">{rotator.gender}</span>
                            </p>
                            <p className="text-gray-600">
                              <strong>Hospital:</strong> {rotator.hospital}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={() => handleToggleVerified(rotator.id, rotator.verified)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${
                              rotator.verified 
                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                            disabled={loading}
                          >
                            <CheckCircle className="w-3 h-3" />
                            {rotator.verified ? 'Mark as Not Verified' : 'Mark as Verified'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({
                              show: true,
                              type: 'rotator',
                              id: rotator.id,
                              name: `${rotator.first_name} ${rotator.last_name}`
                            })}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs flex items-center gap-1"
                          >
                            <User className="w-3 h-3" />
                            Delete Account
                          </button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-gray-700 font-medium mb-2">
                          {coordinatorData?.city === 'All' ? 'All Rotations:' : `Rotations in ${coordinatorData?.city}:`}
                        </p>
                        {rotator.rotation_details && rotator.rotation_details.length > 0 ? (
                          coordinatorData?.city === 'All' ? (
                            // Show ALL rotations for admin
                            rotator.rotation_details.map((rotation, index) => (
                              <div key={index} className="bg-white rounded p-3 mb-2">
                                <p className="font-medium">{rotation.school || 'School not specified'}</p>
                                <p className="text-gray-600 text-sm">
                                  {rotation.city && rotation.state 
                                    ? `${rotation.city}, ${rotation.state}` 
                                    : rotation.zipCode 
                                      ? `Zip: ${rotation.zipCode}` 
                                      : 'Location not specified'}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  {formatDateDisplay(rotation.startDate)} - {formatDateDisplay(rotation.endDate)}
                                </p>
                              </div>
                            ))
                          ) : (
                            // Filter by city for specific coordinator
                            rotator.rotation_details.filter(r => r.city === coordinatorData?.city).map((rotation, index) => (
                              <div key={index} className="bg-white rounded p-3 mb-2">
                                <p className="font-medium">{rotation.school || 'School not specified'}</p>
                                <p className="text-gray-600 text-sm">
                                  {rotation.city && rotation.state 
                                    ? `${rotation.city}, ${rotation.state}` 
                                    : rotation.zipCode 
                                      ? `Zip: ${rotation.zipCode}` 
                                      : 'Location not specified'}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  {formatDateDisplay(rotation.startDate)} - {formatDateDisplay(rotation.endDate)}
                                </p>
                              </div>
                            ))
                          )
                        ) : (
                          <p className="text-gray-500 text-sm">No rotations listed</p>
                        )}
                        {rotator.preferences && (
                          <p className="text-gray-600 text-sm mt-2"><strong>Preferences:</strong> {rotator.preferences}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No rotators looking for housing in {coordinatorData?.city} yet.</p>
              )}
            </div>

            {/* HOSTS SECTION - NOW SECOND */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-semibold text-gray-800">
                  Hosts ({coordinatorData?.hosts?.length || 0})
                </h3>
              </div>
              {coordinatorData?.hosts && coordinatorData.hosts.length > 0 ? (
                <div className="space-y-4">
                  {coordinatorData.hosts.map((host) => (
                    <div key={host.id} className="rounded-lg p-6" style={{backgroundColor: '#D1F2EB', borderColor: '#5DBEA3', borderWidth: '1px'}}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {host.first_name} {host.last_name}
                          </h4>
                          <div className="mt-2 space-y-1 text-sm">
                            <p className="text-gray-600">
                              <Mail className="w-4 h-4 inline mr-2" />
                              {host.email}
                            </p>
                            <p className="text-gray-600">
                              <Phone className="w-4 h-4 inline mr-2" />
                              {host.phone_number}
                            </p>
                            <p className="text-gray-600">
                              <Building className="w-4 h-4 inline mr-2" />
                              {host.institution}
                            </p>
                            <p className="text-gray-600">
                              <MapPin className="w-4 h-4 inline mr-2" />
                              {host.host_city && host.host_state ? `${host.host_city}, ${host.host_state}` : 'Location not specified'}
                              {host.zip_code && ` ${host.zip_code}`}
                              {host.address && ` - ${host.address}`}
                            </p>
                            <p className="text-gray-600">
                              <User className="w-4 h-4 inline mr-2" />
                              Gender: <span className="capitalize">{host.gender}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs ${host.display_publicly ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {host.display_publicly ? 'Public' : 'Private'}
                          </span>
                          <button
                            onClick={() => setDeleteConfirm({
                              show: true,
                              type: 'host',
                              id: host.id,
                              name: `${host.first_name} ${host.last_name}`
                            })}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-xs flex items-center gap-1"
                          >
                            <User className="w-3 h-3" />
                            Delete Account
                          </button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-gray-700"><strong>Description:</strong> {host.description}</p>
                        {host.amenities && <p className="text-gray-600 text-sm mt-1"><strong>Amenities:</strong> {host.amenities}</p>}
                        <p className="text-gray-600 text-sm mt-1">
                          <strong>Available:</strong> {formatDateDisplay(host.available_from)} - {formatDateDisplay(host.available_to)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No hosts in {coordinatorData?.city} yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Contact Us Page
  if (view === 'contact') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
          <button 
            onClick={() => navigateWithLoading(currentUser ? 'dashboard' : 'landing')} 
            className="mb-4 text-sm sm:text-base hover:opacity-80 transition-all"
            style={{color: '#004B87'}}
          >
            ← Back
          </button>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Contact Us</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">Have a question or need help? Send us a message!</p>
          
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2 text-sm"><CheckCircle className="w-5 h-5" />{success}</div>}
          
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                placeholder="Your name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                placeholder="your.email@example.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                rows="6"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                placeholder="How can we help you?"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50 text-sm sm:text-base"
              style={{background: loading ? '#9CA3AF' : 'linear-gradient(to right, #5DBEA3, #4AA896)'}}
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Mission Page
  if (view === 'mission') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-3xl w-full bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
          <button 
            onClick={() => navigateWithLoading(currentUser ? 'dashboard' : 'landing')} 
            className="mb-4 text-sm sm:text-base"
            style={{color: '#004B87'}}
          >
            ← Back
          </button>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{color: '#004B87'}}>
            About ClerkshipConnect
          </h2>
          
          <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none space-y-6 sm:space-y-8">
            {/* Our Story */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4" style={{color: '#5DBEA3'}}>
                Our Story
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Our founders experienced firsthand how difficult it can be to find affordable, inclusive housing during away rotations across the United States. Faced with high costs and limited options, they set out to create a platform that would make it easier for other medical students to access supportive housing during this critical stage of their education. ClerkshipConnect was born from that need, to connect students with others in the healthcare community who are willing to open their homes and reduce the financial burden of medical training.
              </p>
            </div>
            
            {/* How It Works */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4" style={{color: '#5DBEA3'}}>
                How It Works
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Hosts and rotators can sign up and create profiles on ClerkshipConnect. Listings for available housing or rotation needs can be published to the platform, allowing users to browse and connect directly. ClerkshipConnect serves as a space to facilitate these connections. All other terms, including pricing, duration, and expectations, are arranged at the discretion of the hosts and rotators.
              </p>
            </div>
            
            {/* Who It's For */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4" style={{color: '#5DBEA3'}}>
                Who It's For
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                ClerkshipConnect is designed for medical students pursuing away rotations at institutions other than their own. Whether you are traveling across the state or across the country, the platform helps connect you with trusted members of the healthcare community offering housing support during your rotation.
              </p>
            </div>
            
            {/* Contact Us */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border-2" style={{borderColor: '#5DBEA3'}}>
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4" style={{color: '#004B87'}}>
                Contact Us
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Have questions or feedback? We'd love to hear from you.
              </p>
              <button
                onClick={() => navigateWithLoading('contact')}
                className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-all text-sm sm:text-base"
                style={{background: 'linear-gradient(to right, #5DBEA3, #4AA896)'}}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Page Transition Loading Screen
  if (pageTransitioning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" style={{borderColor: '#004B87'}}></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default ClerkshipConnectApp;